import { useEffect, useCallback, useRef, useState } from "react"
import { createPortal } from "react-dom"
import { ChatCircle, X, ArrowsOut, ArrowsIn, ArrowSquareOut, DotsSix, CaretUp, CaretDown } from "@phosphor-icons/react"
import { useChatStore, MIN_EDGE_PADDING, type ChatPosition } from "../../stores/chat-store"
import { useAuthStore, useUIStore } from "../../stores"
import { useViewingMatterStore } from "../../stores/viewing-matter-store"
import { cn } from "../../lib/utils"
import { useRegisterDismissExclusion } from "../ui/dismiss-exclusions-context"
import { useDrag, useViewportClamp } from "../../hooks/useDrag"
import { keys } from "../../keys"

const CHAT_URL = keys.CHAT_URL

// Bridge event types - must match chat app
const BRIDGE_EVENTS = {
  INIT_CONTEXT: "CHAT_INIT_CONTEXT",
  WIDGET_OPENED: "CHAT_WIDGET_OPENED", // Signals chat to reset navigation state
  READY: "CHAT_READY",
  REQUEST_CONTEXT: "CHAT_REQUEST_CONTEXT",
  UNREAD_UPDATE: "CHAT_UNREAD_UPDATE",
  REQUEST_UNREAD: "CHAT_REQUEST_UNREAD",
  THEME_UPDATE: "CHAT_THEME_UPDATE",
}

// How often to poll for unread counts (in ms)
const UNREAD_POLL_INTERVAL = 15000 // 15 seconds

// Widget dimensions for different states
const WIDGET_DIMENSIONS = {
  button: { width: 56, height: 56 },
  minimized: { width: 280, height: 44 },
  expanded: { width: 400, height: 550 },
  expandedMobile: { width: window.innerWidth - 20, height: window.innerHeight - 100 },
}

export function ChatWidget() {
  const iframeRef = useRef<HTMLIFrameElement>(null)
  const [maximized, setMaximized] = useState(false)

  // Element refs for direct DOM manipulation during drag (bypasses React for performance)
  const buttonRef = useRef<HTMLButtonElement>(null)
  const minimizedBarRef = useRef<HTMLDivElement>(null)
  const expandedContainerRef = useRef<HTMLDivElement>(null)

  const {
    open,
    minimized,
    openChat,
    closeChat,
    expandChat,
    position,
    setPosition,
    isDragging,
    setIsDragging,
    primaryColor,
    textColor,
    setThemeColors,
    dockedToNavbar,
    setDockedToNavbar,
    unreadCount,
    hasMentions,
    setUnreadCount,
  } = useChatStore()
  const { user } = useAuthStore()
  const { selectedDivision } = useUIStore()

  // Capture matter context at moment of opening (prevents UI jump if user navigates while chat is open)
  const capturedMatterRef = useRef<{ _id: string; id: string; name: string } | null>(null)
  const hasSentInitialContextRef = useRef(false)
  const viewingMatter = useViewingMatterStore((s) => s.viewingMatter)

  // Determine current widget dimensions based on state
  const getWidgetDimensions = useCallback(() => {
    if (!open) return WIDGET_DIMENSIONS.button
    if (minimized) return WIDGET_DIMENSIONS.minimized
    return WIDGET_DIMENSIONS.expanded
  }, [open, minimized])

  const dimensions = getWidgetDimensions()

  // Direct DOM update callback - bypasses React entirely for smooth dragging
  const handleVisualUpdate = useCallback((pos: ChatPosition) => {
    // Directly manipulate DOM styles - no React re-renders during drag
    const elements = [
      buttonRef.current,
      minimizedBarRef.current,
      expandedContainerRef.current,
      iframeRef.current,
    ]
    elements.forEach((el) => {
      if (el && !maximized) {
        el.style.right = `${pos.x}px`
        el.style.bottom = `${pos.y}px`
      }
    })
  }, [maximized])

  // Drag hook for repositioning
  const { handleMouseDown, handleTouchStart, isPointerDown } = useDrag({
    position,
    onPositionChange: setPosition, // Called once on drag end to persist
    onVisualUpdate: handleVisualUpdate, // Called every RAF during drag
    onDragStart: () => setIsDragging(true),
    onDragEnd: () => setIsDragging(false),
    onClick: () => {
      // Only open chat when clicking the closed button
      if (!open) openChat()
    },
    minEdgePadding: MIN_EDGE_PADDING,
    widgetWidth: dimensions.width,
    widgetHeight: dimensions.height,
    disabled: maximized,
  })

  // Keep widget in viewport on resize
  useViewportClamp(position, setPosition, dimensions.width, dimensions.height, MIN_EDGE_PADDING)

  // Build the CRM context to send to chat app
  const buildCRMContext = useCallback(
    (matter: { _id: string; id: string; name: string } | null = null) => {
      const context: Record<string, unknown> = {}

      // Add user context
      if (user?._id) {
        context.user = {
          _id: user._id,
          display_name: `${user.given_name || ""} ${user.family_name || ""}`.trim(),
          given_name: user.given_name,
          family_name: user.family_name,
          email: user.email,
        }
      }

      // Add division context
      if (selectedDivision) {
        context.division = selectedDivision
      }

      // Add matter context (for navigating to matter's channel)
      if (matter) {
        context.matter = {
          _id: matter._id,
          id: matter.id,
          name: matter.name,
        }
      }

      return context
    },
    [user, selectedDivision]
  )

  // Send context to iframe
  const sendContextToIframe = useCallback(() => {
    if (iframeRef.current?.contentWindow) {
      const context = buildCRMContext(capturedMatterRef.current)

      iframeRef.current.contentWindow.postMessage(
        { type: BRIDGE_EVENTS.INIT_CONTEXT, payload: context },
        CHAT_URL
      )
    }
  }, [buildCRMContext])

  // Request unread count from chat iframe
  const requestUnreadCount = useCallback(() => {
    if (iframeRef.current?.contentWindow) {
      iframeRef.current.contentWindow.postMessage({ type: BRIDGE_EVENTS.REQUEST_UNREAD }, CHAT_URL)
    }
  }, [])

  // Reset context tracking when chat closes
  useEffect(() => {
    if (!open) {
      hasSentInitialContextRef.current = false
      capturedMatterRef.current = null
    }
  }, [open])

  // Send "widget opened" signal and context when chat opens
  useEffect(() => {
    // Skip if chat is closed or we already sent initial context
    if (!open || hasSentInitialContextRef.current) {
      return
    }

    // Capture viewing matter at moment of opening (no loading to wait for - store is set by MatterDetail)
    capturedMatterRef.current = viewingMatter
    hasSentInitialContextRef.current = true

    // Small delay to ensure iframe is ready
    setTimeout(() => {
      // First, signal that widget was opened (resets chat's navigation state)
      if (iframeRef.current?.contentWindow) {
        iframeRef.current.contentWindow.postMessage(
          { type: BRIDGE_EVENTS.WIDGET_OPENED },
          CHAT_URL
        )
      }
      // Then send context with matter info
      sendContextToIframe()
    }, 0)
  }, [open, viewingMatter, sendContextToIframe])

  // Listen for messages from chat iframe
  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      // Validate origin
      if (event.origin !== CHAT_URL) return

      const { type, payload } = event.data || {}

      switch (type) {
        case BRIDGE_EVENTS.READY:
          // Chat iframe is ready - request unread count and send context if open
          requestUnreadCount()
          if (open) {
            sendContextToIframe()
          }
          break
        case BRIDGE_EVENTS.REQUEST_CONTEXT:
          if (open) {
            sendContextToIframe()
          }
          break
        case BRIDGE_EVENTS.UNREAD_UPDATE:
          setUnreadCount(payload?.totalUnread || 0, payload?.hasMentions || false)
          break
        case BRIDGE_EVENTS.THEME_UPDATE:
          setThemeColors(payload?.primaryColor || null, payload?.textColor || null)
          break
        default:
          break
      }
    }

    window.addEventListener("message", handleMessage)
    return () => window.removeEventListener("message", handleMessage)
  }, [open, sendContextToIframe, requestUnreadCount, setThemeColors, setUnreadCount])

  // Poll for unread counts periodically
  useEffect(() => {
    const initialTimeout = setTimeout(() => {
      requestUnreadCount()
    }, 2000)

    const pollInterval = setInterval(() => {
      requestUnreadCount()
    }, UNREAD_POLL_INTERVAL)

    return () => {
      clearTimeout(initialTimeout)
      clearInterval(pollInterval)
    }
  }, [requestUnreadCount])

  // Format unread count for display
  const displayCount = unreadCount > 99 ? "99+" : unreadCount

  const showBadge = unreadCount > 0 && (!open || minimized)

  // Determine widget state
  const isExpanded = open && !minimized

  // Ref for registering chat widget as excluded from modal dismiss detection
  const chatContainerRef = useRef<HTMLDivElement>(null)

  // Register chat widget with dismiss exclusions context
  // This tells all Radix UI components to ignore clicks on this element when detecting "outside" clicks
  useRegisterDismissExclusion(chatContainerRef)

  // Position style for the widget (when not maximized)
  const positionStyle = maximized
    ? {}
    : {
        right: position.x,
        bottom: position.y,
      }

  // Render to document.body via portal to escape any parent stacking contexts
  // Container has pointer-events-none so clicks pass through, but interactive elements have pointer-events-auto
  // This ensures the chat widget is always on top and clickable regardless of other modals/sheets
  return createPortal(
    <div
      ref={chatContainerRef}
      className="fixed inset-0 pointer-events-none z-[99999]"
      style={{ isolation: "isolate" }}
    >
      {/* Floating button when chat is closed (hidden when docked to navbar) */}
      {!open && !dockedToNavbar && (
        <button
          ref={buttonRef}
          onMouseDown={handleMouseDown}
          onTouchStart={handleTouchStart}
          className={cn(
            "pointer-events-auto",
            "fixed w-14 h-14 rounded-full",
            !primaryColor && "bg-[hsl(222,33%,32%)] dark:bg-sky-600",
            "text-white shadow-lg shadow-black/40 dark:shadow-primary/40",
            "flex items-center justify-center",
            "border border-gray-600 dark:border-white/90",
            "hover:scale-[1.08] hover:shadow-xl hover:shadow-black/50 dark:hover:shadow-primary/50",
            "active:scale-[0.98]",
            !isPointerDown && "transition-all duration-300 ease-out",
            isDragging ? "cursor-grabbing" : "cursor-grab"
          )}
          style={{
            ...positionStyle,
            ...(primaryColor && { backgroundColor: primaryColor }),
            ...(textColor && { color: textColor }),
          }}
        >
          <ChatCircle className="h-6 w-6" weight="fill" />
          {showBadge && (
            <span
              className={cn(
                "absolute -top-1 -right-1 min-w-[20px] h-5 px-1.5",
                "rounded-full bg-destructive text-white",
                "text-[11px] font-semibold",
                "flex items-center justify-center",
                "border-2 border-white shadow-md",
                "animate-in zoom-in-50 duration-300",
                hasMentions && "animate-pulse"
              )}
            >
              {displayCount}
            </span>
          )}
        </button>
      )}

      {/* Minimized bar */}
      {open && minimized && (
        <div
          ref={minimizedBarRef}
          className={cn(
            "pointer-events-auto",
            "fixed w-[280px]",
            !primaryColor && "bg-[hsl(222,33%,32%)] dark:bg-sky-600",
            "rounded-lg shadow-lg shadow-black/15",
            !isPointerDown && "animate-in slide-in-from-bottom-2 duration-200"
          )}
          style={{
            ...positionStyle,
            ...(primaryColor && { backgroundColor: primaryColor }),
            ...(textColor && { color: textColor }),
          }}
        >
          <div className={cn("flex items-center justify-between px-4 pt-2.5 pb-3.5", !textColor && "text-white")}>
            <div
              className={cn(
                "flex items-center gap-1",
                isDragging ? "cursor-grabbing" : "cursor-grab"
              )}
              onMouseDown={handleMouseDown}
              onTouchStart={handleTouchStart}
            >
              <DotsSix className="h-4 w-4 opacity-60" weight="bold" />
              <span className="font-semibold text-sm flex items-center gap-2">
                <ChatCircle className="h-4 w-4" weight="fill" />
                Chat
                {showBadge && (
                  <span
                    className={cn(
                      "ml-1 min-w-[18px] h-[18px] px-1.5",
                      "rounded-full bg-destructive",
                      "text-[10px] font-semibold",
                      "inline-flex items-center justify-center",
                      hasMentions && "animate-pulse"
                    )}
                  >
                    {displayCount}
                  </span>
                )}
              </span>
            </div>
            <div className="flex items-center gap-3">
              <button
                onClick={expandChat}
                className="opacity-80 hover:opacity-100 transition-opacity p-1 rounded hover:bg-white/15"
                title="Expand"
              >
                <ArrowsOut className="h-4 w-4" />
              </button>
              <button
                onClick={closeChat}
                className="opacity-80 hover:opacity-100 transition-opacity p-1 rounded hover:bg-white/15"
                title="Close"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Expanded/Maximized chat window */}
      {isExpanded && (
        <>
          {/* Backdrop overlay when maximized */}
          {maximized && (
            <div
              className="pointer-events-auto fixed inset-0 bg-black/50 animate-in fade-in duration-200 backdrop-blur-sm"
              onClick={() => setMaximized(false)}
            />
          )}
          <div
            ref={expandedContainerRef}
            className={cn(
              "pointer-events-auto",
              "fixed bg-background rounded-lg overflow-hidden",
              "flex flex-col shadow-2xl shadow-black/20",
              "ring-1 ring-gray-600 dark:ring-white/90",
              !isPointerDown && "animate-in slide-in-from-bottom-2 duration-250",
              maximized
                ? "bottom-6 right-6 w-[calc(100vw-48px)] h-[calc(100vh-48px)] rounded-xl"
                : "w-[400px] h-[550px]",
              !maximized &&
                "max-sm:!inset-0 max-sm:w-screen max-sm:h-screen max-sm:rounded-none",
              maximized &&
                "max-sm:bottom-0 max-sm:right-0 max-sm:w-screen max-sm:h-screen max-sm:rounded-none"
            )}
            style={maximized ? {} : positionStyle}
          >
            <div
              className={cn(
                "flex items-center justify-between px-4 pt-2.5 pb-3.5",
                !primaryColor && "bg-[hsl(222,33%,32%)] dark:bg-sky-600",
                !textColor && "text-white",
                maximized && "px-5 py-4",
                !maximized && (isDragging ? "cursor-grabbing" : "cursor-grab")
              )}
              style={{
                ...(primaryColor && { backgroundColor: primaryColor }),
                ...(textColor && { color: textColor }),
              }}
              onMouseDown={!maximized ? handleMouseDown : undefined}
              onTouchStart={!maximized ? handleTouchStart : undefined}
            >
              <span
                className={cn("font-semibold flex items-center gap-2", maximized ? "text-base" : "text-sm")}
              >
                {!maximized && <DotsSix className="h-4 w-4 opacity-60" weight="bold" />}
                <ChatCircle
                  className={cn("weight-fill", maximized ? "h-[18px] w-[18px]" : "h-4 w-4")}
                  weight="fill"
                />
                Chat
              </span>
              <div className="flex items-center gap-3">
                <button
                  onClick={() => setDockedToNavbar(!dockedToNavbar)}
                  className="opacity-80 hover:opacity-100 transition-opacity p-1 rounded hover:bg-white/15"
                  title={dockedToNavbar ? "Show floating button" : "Hide floating button"}
                >
                  {dockedToNavbar ? <CaretDown className="h-4 w-4" /> : <CaretUp className="h-4 w-4" />}
                </button>
                <a
                  href={CHAT_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="opacity-80 hover:opacity-100 transition-opacity p-1 rounded hover:bg-white/15"
                  title="Open in new tab"
                >
                  <ArrowSquareOut className="h-4 w-4" />
                </a>
                {maximized ? (
                  <button
                    onClick={() => setMaximized(false)}
                    className="opacity-80 hover:opacity-100 transition-opacity p-1 rounded hover:bg-white/15"
                    title="Collapse"
                  >
                    <ArrowsIn className="h-4 w-4" />
                  </button>
                ) : (
                  <button
                    onClick={() => setMaximized(true)}
                    className="opacity-80 hover:opacity-100 transition-opacity p-1 rounded hover:bg-white/15"
                    title="Expand"
                  >
                    <ArrowsOut className="h-4 w-4" />
                  </button>
                )}
                <button
                  onClick={() => {
                    setMaximized(false)
                    closeChat()
                  }}
                  className="opacity-80 hover:opacity-100 transition-opacity p-1 rounded hover:bg-white/15"
                  title="Close"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>
            </div>
            <div className="flex-1 overflow-hidden bg-muted" />
          </div>
        </>
      )}

      {/* Single iframe - always mounted for socket connection, positioned based on state */}
      <iframe
        ref={iframeRef}
        src={CHAT_URL}
        title="Chat"
        className={cn(
          "border-none",
          // Disable pointer events during drag so iframe doesn't capture mouse
          isPointerDown && "pointer-events-none",
          // Hidden state - no pointer events
          !isExpanded && "fixed w-px h-px -left-[9999px] -top-[9999px] opacity-0",
          // Visible expanded state - enable pointer events
          isExpanded && !maximized && "pointer-events-auto fixed w-[400px] h-[calc(550px-41px)] rounded-b-lg max-sm:!left-0 max-sm:!right-0 max-sm:!bottom-0 max-sm:w-screen max-sm:h-[calc(100vh-41px)] max-sm:rounded-none",
          // Visible maximized state - enable pointer events
          isExpanded &&
            maximized &&
            "pointer-events-auto fixed bottom-6 right-6 w-[calc(100vw-48px)] h-[calc(100vh-48px-49px)] rounded-b-xl max-sm:bottom-0 max-sm:right-0 max-sm:w-screen max-sm:h-[calc(100vh-49px)] max-sm:rounded-none"
        )}
        style={
          isExpanded && !maximized
            ? {
                right: position.x,
                bottom: position.y,
              }
            : {}
        }
      />
    </div>,
    document.body
  )
}
